import { type FC } from "react"

import {
	type Plan as PlanType,
	type SubscriptionPaymentInterval,
	type SubscriptionPlan,
} from "@forento/core/models/payment"
import { type FreePlatformPlan, type RegularPlatformPlan } from "@forento/core/models/platform"
import Tooltip from "@forento/ui/components/Tooltip"

import Button, { type ButtonVariant } from "~/components/Button"
import { useAlert } from "~/contexts/AlertContext"
import { useUser } from "~/contexts/UserContext"
import { query } from "~/utilities/trpc"

import {
	PlanBillingInterval,
	default as PlanContainer,
	PlanDescription,
	PlanDivider,
	PlanInterval,
	PlanPrice,
	PlanTitle,
	PlanUspContainer,
	PlanUspIcon,
	PlanUspIconContainer,
	PlanUsps,
	PlanUspText,
} from "../Plan"

type PlatformPlan = FreePlatformPlan | RegularPlatformPlan
type Props = {
	plan: PlanType
	currentPlan: SubscriptionPlan | null
	platformPlan: PlatformPlan
	isMonthly: boolean
	isSubmitting: boolean
	isDisabled: boolean
	onSelectPlan: (paymentInterval: SubscriptionPaymentInterval) => void
	onCancelSubscription: () => void
	onResumeSubscription: () => void
}
const Plan: FC<Props> = ({
	plan,
	currentPlan,
	platformPlan,
	isMonthly,
	isSubmitting,
	isDisabled,
	onSelectPlan,
	onCancelSubscription,
	onResumeSubscription,
}) => {
	const platform = useUser().user!.platform!
	const alert = useAlert()

	const billingPortalUrl = query.payment.creator.getBillingPortalUrl.useMutation({
		async onSuccess(url) {
			if (url === null) {
				console.error("Failed to get billing portal URL!")
				await alert.show("Error", "Failed to open payment method management page. Please contact support.")
				return
			}
			window.open(url, "_blank", "noopener,noreferrer")
		},
		async onError(error) {
			console.error(error)
			await alert.show("Error", "Failed to open payment method management page. Please try again later.")
		},
	})

	const billingInterval: SubscriptionPaymentInterval = isMonthly ? "month" : "year"
	const isCurrentPlanInterval =
		currentPlan !== null && currentPlan.id === plan.id && currentPlan.price.interval === billingInterval

	const chargedPrice = isCurrentPlanInterval
		? { month: currentPlan.price.amount, year: currentPlan.price.amount / 12 }
		: plan.price
			? { month: plan.price.month, year: plan.price.year / 12 }
			: null
	const catalogPrice = plan.price ? { month: plan.price.month, year: plan.price.year / 12 } : null

	const isBokaderoDiscountedScale = platform.isFromBokadero && plan.id === 2 && catalogPrice !== null
	const displayedPrice =
		isBokaderoDiscountedScale && !isCurrentPlanInterval
			? catalogPrice[billingInterval] / 2
			: (chargedPrice?.[billingInterval] ?? null)
	const callToAction = getCallToAction({
		plan,
		currentPlan,
		platformPlan,
		trialEndDate: platform.trialEndDate,
		isMonthly,
		isSubmitting,
		isDisabled,
		onSelectPlan,
		onCancelSubscription,
		onResumeSubscription,
	})

	const callToActionButton = (
		<Button
			key={callToAction.key}
			variant={callToAction.variant}
			onClick={callToAction.onClick}
			isLoading={callToAction.isLoading}
			isDisabled={callToAction.isDisabled}
			className="w-full"
		>
			{callToAction.label}
		</Button>
	)

	return (
		<PlanContainer>
			<PlanTitle>{plan.title}</PlanTitle>
			{chargedPrice ? (
				<PlanPrice>
					${formatPrice(displayedPrice)}
					<PlanInterval>/month</PlanInterval>
				</PlanPrice>
			) : (
				<PlanPrice>Free</PlanPrice>
			)}
			{isBokaderoDiscountedScale && (
				<div className="text-center text-sm text-gray-600">
					<span className="line-through">${formatPrice(catalogPrice[billingInterval])}</span> original price
				</div>
			)}
			<PlanBillingInterval>
				{chargedPrice && <>Billed {billingInterval === "month" ? "monthly" : "annually"}</>}
			</PlanBillingInterval>
			{isBokaderoDiscountedScale && (
				<div className="text-center text-sm font-medium text-emerald-700">50% off for Bokadero members</div>
			)}
			<PlanDescription>{plan.description}</PlanDescription>
			<PlanDivider />
			<PlanUsps>
				{plan.usps.map((usp, index) => (
					<PlanUspContainer key={index}>
						<PlanUspIconContainer>
							<PlanUspIcon />
						</PlanUspIconContainer>
						<PlanUspText>{usp}</PlanUspText>
					</PlanUspContainer>
				))}
			</PlanUsps>
			<PlanDivider />
			{currentPlan && currentPlan.id === plan.id && currentPlan.price.interval === billingInterval && (
				<div className="mb-3">
					<Button
						variant="primary"
						onClick={() => billingPortalUrl.mutate()}
						isLoading={billingPortalUrl.isPending}
					>
						Manage payment method
					</Button>
				</div>
			)}
			{callToAction.tooltip ? (
				<Tooltip tooltip={callToAction.tooltip} className="w-full">
					{callToActionButton}
				</Tooltip>
			) : (
				callToActionButton
			)}
		</PlanContainer>
	)
}

function formatPrice(price: number | null): string {
	if (price === null) return "0"
	const formatted = price.toFixed(2)
	return formatted.endsWith(".00") ? formatted.slice(0, -3) : formatted
}

function getCallToAction({
	plan,
	currentPlan,
	platformPlan,
	trialEndDate,
	isMonthly,
	isSubmitting,
	isDisabled,
	onSelectPlan,
	onCancelSubscription,
	onResumeSubscription,
}: {
	plan: PlanType
	currentPlan: SubscriptionPlan | null
	platformPlan: PlatformPlan
	trialEndDate: Date | null
	isMonthly: boolean
	isSubmitting: boolean
	isDisabled: boolean
	onSelectPlan: (paymentInterval: SubscriptionPaymentInterval) => void
	onCancelSubscription: () => void
	onResumeSubscription: () => void
}): {
	key: string
	variant: ButtonVariant
	label: string
	onClick?: () => void
	isLoading?: boolean
	isDisabled?: boolean
	tooltip?: string
} {
	const currentInterval: SubscriptionPaymentInterval = isMonthly ? "month" : "year"
	const hasTrial = trialEndDate !== null && trialEndDate > new Date()

	if (plan.id === (currentPlan?.id ?? null)) {
		if (plan.price === null) {
			return {
				key: "current",
				variant: "primary",
				label: "Current plan",
				isDisabled: true,
			}
		}

		if (currentPlan && currentPlan.price.interval !== currentInterval) {
			if (hasTrial) {
				return {
					key: "trial",
					variant: "primary",
					label: "Switch to plan",
					onClick: () => {},
					isDisabled: true,
					isLoading: false,
					tooltip: "You can't switch to another plan during your free trial.",
				}
			}
			return {
				key: "switch-interval",
				variant: isMonthly ? "secondary" : "primary",
				label: `Switch to ${isMonthly ? "monthly" : "annual"} payment`,
				onClick: () => onSelectPlan(currentInterval),
				isLoading: isSubmitting,
				isDisabled: isDisabled || plan.price === null,
			}
		}

		if (platformPlan.type === "regular" && platformPlan.endDate) {
			return {
				key: "resume",
				variant: "secondary",
				label: "Resume subscription",
				onClick: onResumeSubscription,
				isDisabled,
				isLoading: isSubmitting,
			}
		}

		return {
			key: "cancel",
			variant: "secondary-danger",
			label: "Cancel subscription",
			onClick: onCancelSubscription,
			isDisabled: isDisabled || plan.price === null,
			isLoading: isSubmitting,
		}
	}

	if (currentPlan === null && plan.price !== null && (trialEndDate === null || hasTrial)) {
		return {
			key: "trial",
			variant: "primary",
			label: trialEndDate === null ? "Start free trial" : "Continue free trial",
			onClick: () => onSelectPlan(currentInterval),
			isLoading: isSubmitting,
			isDisabled,
		}
	}

	if (hasTrial) {
		return {
			key: "trial",
			variant: "primary",
			label: "Switch to plan",
			onClick: () => {},
			isDisabled: true,
			isLoading: false,
			tooltip: "You can't switch to another plan during your free trial.",
		}
	}

	const isMoreProfitable =
		currentPlan === null || (plan.price?.[currentPlan.price.interval] ?? 0) > currentPlan.price.amount

	return {
		key: "switch",
		variant: isMoreProfitable ? "primary" : "secondary",
		label: "Switch to plan",
		onClick: plan.price ? () => onSelectPlan(currentInterval) : onCancelSubscription,
		isDisabled,
		isLoading: isSubmitting,
	}
}

export default Plan
